import { useState } from 'react'
import { X } from 'lucide-react'
import { Select } from '../ui/Select'

interface InviteTeamMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onInvite: (payload: InviteTeamMemberFormData) => Promise<void>
  isSubmitting: boolean
}

export interface InviteTeamMemberFormData {
  fullName: string
  email: string
  role: 'manager' | 'employee'
  includeDeviceSetup?: boolean
  deviceOsType?: 'windows' | 'mac' | 'linux'
  deviceName?: string
}

const defaultFormState: InviteTeamMemberFormData = {
  fullName: '',
  email: '',
  role: 'employee',
  includeDeviceSetup: false,
  deviceOsType: 'windows',
  deviceName: ''
}

export const InviteTeamMemberModal = ({ isOpen, onClose, onInvite, isSubmitting }: InviteTeamMemberModalProps) => {
  const [formState, setFormState] = useState<InviteTeamMemberFormData>(defaultFormState)
  const [error, setError] = useState<string | null>(null)

  const resetForm = () => {
    setFormState(defaultFormState)
    setError(null)
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }

  const handleChange = (field: keyof InviteTeamMemberFormData, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    console.log('InviteTeamMemberModal: Submitting invitation', {
      includeDeviceSetup: formState.includeDeviceSetup,
      deviceOsType: formState.deviceOsType,
      deviceName: formState.deviceName
    })

    if (!formState.fullName.trim() || !formState.email.trim()) {
      setError('Full name and email are required.')
      return
    }

    try {
      await onInvite(formState)
      resetForm()
      onClose()
    } catch (err) {
      setError('Failed to send invitation. Please try again.')
      console.error('InviteTeamMemberModal: Failed to send invite', err)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={handleClose} />

      <div className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Invite Team Member</h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close invite modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              value={formState.fullName}
              onChange={(event) => handleChange('fullName', event.target.value)}
              placeholder="Full Name"
              className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={formState.email}
              onChange={(event) => handleChange('email', event.target.value)}
              placeholder="Email address"
              className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Role</label>
            <Select
              value={formState.role}
              onChange={(event) => handleChange('role', event.target.value)}
              className="w-full"
            >
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
            </Select>
          </div>

          {/* Device Setup Section - Only show for employees */}
          {formState.role === 'employee' && (
            <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formState.includeDeviceSetup}
                  onChange={(e) => handleChange('includeDeviceSetup', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-900"
                />
                <span className="text-sm font-medium text-gray-700">
                  Send device setup link
                </span>
              </label>

              {formState.includeDeviceSetup && (
                <div className="space-y-3 pl-7">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">
                      Operating System
                    </label>
                    <Select
                      value={formState.deviceOsType}
                      onChange={(e) => handleChange('deviceOsType', e.target.value)}
                      className="w-full"
                    >
                      <option value="windows">Windows</option>
                      <option value="mac">macOS</option>
                      <option value="linux">Linux</option>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">
                      Device Name (optional)
                    </label>
                    <input
                      type="text"
                      value={formState.deviceName}
                      onChange={(e) => handleChange('deviceName', e.target.value)}
                      placeholder="e.g., John's Laptop"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    Employee will receive an email with the agent installer link
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Sending invite...' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  )
}
